import React, { useState } from 'react';
import { StickyNote, Eye, Lock } from 'lucide-react';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { Modal } from '@/components/ui/Modal/Modal';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Button } from '@/components/ui/Button/Button';
import { Switch } from '@/components/ui/Switch/Switch';
import { VStack, HStack } from '@/components/ui/Stack';
import { cn } from '@/lib/utils';

/** Must match SHORTLIST_NOTE_MAX_LENGTH on the server (shortlistNotes.ts). */
export const NOTE_MAX_LENGTH = 5000;

/**
 * Saves the one hotel note shared by Research (Step 1), RFPs (Step 2) and
 * Selection (Step 3) together with its Internal / Client-facing visibility.
 */
export type SaveHotelNote = (notes: string, clientFacing: boolean) => void | Promise<void>;

export function NoteVisibilityBadge({ clientFacing, className }: { clientFacing: boolean; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4',
        clientFacing ? 'bg-secondary-100 text-secondary-800' : 'bg-gray-100 text-gray-600',
        className
      )}
    >
      {clientFacing ? <Eye className="w-3 h-3" aria-hidden /> : <Lock className="w-3 h-3" aria-hidden />}
      {clientFacing ? 'Client-facing' : 'Internal'}
    </span>
  );
}

interface NotesEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  venueName: string;
  notes: string | null | undefined;
  clientFacing: boolean | undefined;
  onSave: SaveHotelNote;
}

export function NotesEditorModal({ isOpen, onClose, venueName, notes, clientFacing, onSave }: NotesEditorModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Notes for ${venueName}`} size="md">
      {/* Remount per opening so the draft always starts from the saved note. */}
      {isOpen && (
        <NotesEditorForm notes={notes} clientFacing={clientFacing} onSave={onSave} onClose={onClose} />
      )}
    </Modal>
  );
}

function NotesEditorForm({
  notes,
  clientFacing,
  onSave,
  onClose,
}: Pick<NotesEditorModalProps, 'notes' | 'clientFacing' | 'onSave' | 'onClose'>) {
  const [draft, setDraft] = useState(notes || '');
  const [isClientFacing, setIsClientFacing] = useState(clientFacing === true);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSave() {
    setIsSaving(true);
    try {
      await onSave(draft, isClientFacing);
      onClose();
    } catch (error) {
      // The caller shows the error toast; keep the modal open with the draft.
      console.error('Failed to save notes:', error);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <VStack gap={4}>
      <div className="flex flex-col gap-1 sm:items-end">
        <div className="flex items-center gap-2 text-sm" data-testid="note-visibility-toggle">
          <span className={cn(!isClientFacing ? 'font-semibold text-foreground' : 'text-muted-foreground')}>Internal</span>
          <Switch
            checked={isClientFacing}
            onChange={(e) => setIsClientFacing(e.target.checked)}
            aria-label="Client-facing note"
          />
          <span className={cn(isClientFacing ? 'font-semibold text-secondary-700' : 'text-muted-foreground')}>Client-facing</span>
        </div>
        <p className="text-xs text-muted-foreground sm:text-right">
          {isClientFacing
            ? 'Shown beside this hotel on the research link and the proposal.'
            : 'Only your team can see this note.'}
        </p>
      </div>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Add notes about this venue...&#10;&#10;Example:&#10;- Good for executives&#10;- Close to conference center&#10;- Check room block availability"
        rows={6}
        maxLength={NOTE_MAX_LENGTH}
        className="resize-none"
        aria-label="Hotel notes"
      />
      <p className="text-xs text-muted-foreground">
        {draft.length}/{NOTE_MAX_LENGTH} characters · the same note appears in Research, RFPs and Selection
      </p>
      <HStack gap={2} justify="end">
        <Button variant="secondary" size="sm" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave} isLoading={isSaving}>
          Save Notes
        </Button>
      </HStack>
    </VStack>
  );
}

interface NoteIconProps {
  notes: string | null | undefined;
  clientFacing?: boolean;
  venueName: string;
  onUpdateNotes: SaveHotelNote;
  className?: string;
}

export function NoteIcon({ notes, clientFacing = false, venueName, onUpdateNotes, className }: NoteIconProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasNotes = !!notes && notes.trim().length > 0;
  const tooltip = hasNotes ? `${clientFacing ? 'Client-facing' : 'Internal'} note: ${notes}` : 'Click to add notes';

  return (
    <>
      <SimpleTooltip content={tooltip} side="top">
        <button
          onClick={() => setIsModalOpen(true)}
          className={cn(
            'relative p-2 rounded transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center',
            'hover:bg-gray-100 dark:hover:bg-gray-800',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
            className
          )}
          aria-label={hasNotes ? `Edit notes (${clientFacing ? 'client-facing' : 'internal'})` : 'Add notes'}
          data-client-facing={hasNotes && clientFacing ? 'true' : undefined}
        >
          <StickyNote
            className={cn(
              'w-4 h-4 transition-colors',
              hasNotes ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
            )}
          />
          {hasNotes && clientFacing && (
            <span className="absolute top-1.5 right-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-secondary-600 text-white">
              <Eye className="h-2.5 w-2.5" aria-hidden />
            </span>
          )}
        </button>
      </SimpleTooltip>

      <NotesEditorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        venueName={venueName}
        notes={notes}
        clientFacing={clientFacing}
        onSave={onUpdateNotes}
      />
    </>
  );
}

interface HotelNotesPanelProps {
  notes: string | null | undefined;
  clientFacing?: boolean;
  venueName: string;
  onUpdateNotes: SaveHotelNote;
  isDark?: boolean;
}

/**
 * Permanent notes box for the Selection (Step 3) card: shows the same hotel
 * note as Research and RFPs and edits it through the same editor.
 */
export function HotelNotesPanel({ notes, clientFacing = false, venueName, onUpdateNotes, isDark = false }: HotelNotesPanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const text = notes?.trim() ? notes : null;

  return (
    <div
      className={cn('rounded-lg border p-3 text-sm', isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200')}
      data-testid="hotel-notes-panel"
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StickyNote className={cn('w-4 h-4', text ? 'text-primary-600' : 'text-gray-400')} aria-hidden />
          <span className={cn('font-medium', isDark ? 'text-gray-200' : 'text-gray-900')}>Notes</span>
          {text && <NoteVisibilityBadge clientFacing={clientFacing} />}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setIsModalOpen(true)} className="h-8 px-2 text-xs">
          {text ? 'Edit notes' : 'Add notes'}
        </Button>
      </div>
      {text ? (
        <p className={cn('whitespace-pre-wrap', isDark ? 'text-gray-300' : 'text-gray-700')}>{text}</p>
      ) : (
        <p className="text-muted-foreground">No notes yet.</p>
      )}
      <NotesEditorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        venueName={venueName}
        notes={notes}
        clientFacing={clientFacing}
        onSave={onUpdateNotes}
      />
    </div>
  );
}
